import type {
  OccurrenceResponseStatus,
  DayTasksGroupResponseTimeOfDay,
} from '@kidshome/api-contract';

/**
 * The canonical Morning → Afternoon → Evening → Any ordering for a day's chore buckets, shared by the
 * kid Home day board ({@link TodayPage}) and the {@link TaskBoard} so the two surfaces always sort the
 * time-of-day groups identically. Mirrors the server's `TIME_OF_DAY_BUCKETS` grouping order.
 */
export const BUCKET_ORDER: DayTasksGroupResponseTimeOfDay[] = [
  'morning',
  'afternoon',
  'evening',
  'any',
];

/**
 * Whether the CHILD has finished their part of a chore — the kid-facing "done" shared by the Today
 * board's check button and the kid's "Chores today" progress, so the two never disagree.
 *
 * Includes `pending_approval`: once the kid taps done they've completed their part (it just awaits a
 * grown-up's OK), so it stays "done" from the child's perspective and counts toward their daily
 * progress. `skipped` is an excused/standin completion. `rejected` is NOT done — a grown-up asked for
 * a change, so the kid still has it to redo.
 *
 * This is deliberately DIFFERENT from the grown-up surfaces (the week agenda's `countStatuses`,
 * `TodaySummary`), which keep a SEPARATE "awaiting approval / waiting" bucket — those answer "what
 * still needs MY review", not "did the child finish their chores".
 */
export function isChoreDoneByChild(status: OccurrenceResponseStatus): boolean {
  return status === 'approved' || status === 'skipped' || status === 'pending_approval';
}

/**
 * Whether the kid may UNDO this occurrence (ADR-0141): it's awaiting a grown-up (`pending_approval`,
 * nothing credited) or it auto-credited (`approved` with no explicit approver — `approvedById === null`).
 * A grown-up's manual approval (`approvedById` set) is never kid-undoable. Mirrors the server's
 * `uncomplete` rule; shared by the Today board and the Missions shelf.
 */
export function canUndoByChild(occ: {
  status: OccurrenceResponseStatus;
  approvedById: string | null;
}): boolean {
  return (
    occ.status === 'pending_approval' || (occ.status === 'approved' && occ.approvedById === null)
  );
}
